// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomQuoteModel, GroupLineModel } from "@bg/App/Services/CustomQuoteService";
import { CustomAppState } from "@bg/App/Store/Reducers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useCallback, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    labour: state.pdfSettings.parameters.ThirdPartyQuoteLabourCharges,
    freight: state.pdfSettings.parameters.ThirdPartyQuoteFreightCharges,
    taxType: state.pdfSettings.parameters.ThirdPartyQuoteTaxDisplayOption,
});

interface OwnProps {
    quote: CustomQuoteModel;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface CustomQuoteTotalStyles {
    container?: InjectableCss;
    innerWrapper?: InjectableCss;
    row?: InjectableCss;
    label?: TypographyPresentationProps;
    value?: TypographyPresentationProps;
    quoteTotalWrapper?: InjectableCss;
    quoteTotalValue?: TypographyPresentationProps;
}
export const styles: CustomQuoteTotalStyles = {
    container: {
        css: css`
            display: flex;
            justify-content: flex-end;
            padding-top: 28px;
            margin-top: 50px;
            border-top: 1px solid ${getColor("common.ui2")};
        `,
    },
    innerWrapper: {
        css: css`
            @media (max-width: 600px) {
                width: 100%;
            }
        `,
    },
    row: {
        css: css`
            min-width: 230px;
            display: flex;
            justify-content: space-between;
            margin-bottom: 3px;
            @media (max-width: 600px) {
                width: 100%;
            }
        `,
    },
    label: {
        size: "12px",
        lineHeight: "14px",
        css: css`
            white-space: nowrap;
        `,
    },
    value: {
        size: "13px",
        lineHeight: "15px",
        weight: "bold",
        css: css`
            display: block;
            flex-grow: 1;
            text-align: right;
            padding-left: 5px;
        `,
    },
    quoteTotalWrapper: {
        css: css`
            min-width: 230px;
            padding-top: 20px;
            margin-top: 20px;
            border-top: 1px solid ${getColor("common.ui2")};
            display: flex;
            align-items: center;
            justify-content: space-between;
        `,
    },
    quoteTotalValue: {
        size: "18px",
        lineHeight: "21px",
        weight: "bold",
        css: css`
            text-align: right;
            padding-left: 5px;
        `,
    },
};

const QuoteTotal = ({ quote, taxType, labour, freight }: Props) => {
    const [quoteTotal, setQuoteTotal] = useState(0);
    const [tax, setTaxValue] = useState(0);

    const getGrandTotal = useCallback((): number => {
        let groupTotal = 0;

        if (quote && quote.groupLinesModel) {
            Object.keys(quote.groupLinesModel).forEach(group => {
                quote.groupLinesModel[group].forEach(line => {
                    const lineTotal = line.totalQuotePrice || 0;
                    groupTotal += lineTotal;
                    if (line.relatedItems && line.relatedItems.length > 0) {
                        line.relatedItems.forEach(related => {
                            const relatedTotal = related.totalQuotePrice || 0;
                            groupTotal += relatedTotal;
                        });
                    }
                });
            });
        }
        return groupTotal;
    }, [quote]);

    useEffect(() => {
        if (quote) {
            const freightCharges = !Number.isNaN(parseFloat(freight)) ? parseFloat(freight) : 0;
            const labourCharges = !Number.isNaN(parseFloat(labour)) ? parseFloat(labour) : 0;
            const subGrandTotal = getGrandTotal() + labourCharges;
            const taxValue =
                (taxType === "NoTax" ? 0 : taxType === "Hst" ? quote.hstTaxValue : quote.gstTaxValue) * subGrandTotal;
            setTaxValue(taxValue);
            const quoteGrandTotal = freightCharges + labourCharges + getGrandTotal() + taxValue;
            setQuoteTotal(quoteGrandTotal);
        }
    }, [taxType, labour, freight, quote, getGrandTotal]);

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.innerWrapper}>
                <StyledWrapper {...styles.row}>
                    <Typography {...styles.label}>{translate("Project Subtotal:")}</Typography>
                    <Typography {...styles.value}>
                        <LocalizedCurrency amount={getGrandTotal()} />
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.row}>
                    <Typography {...styles.label}>{translate("Labour:")}</Typography>
                    <Typography {...styles.value}>
                        <LocalizedCurrency amount={labour} />
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.row}>
                    <Typography {...styles.label}>{translate("Freight:")}</Typography>
                    <Typography {...styles.value}>
                        <LocalizedCurrency amount={freight} />
                    </Typography>
                </StyledWrapper>
                {taxType !== "NoTax" && (
                    <StyledWrapper {...styles.row}>
                        <Typography {...styles.label}>{translate("{0} Tax:", taxType?.toUpperCase())}</Typography>
                        <Typography {...styles.value}>
                            <LocalizedCurrency amount={tax} />
                        </Typography>
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.quoteTotalWrapper}>
                    <Typography {...styles.label}>{translate("Quote Total:")}</Typography>
                    <Typography {...styles.quoteTotalValue}>
                        <LocalizedCurrency amount={quoteTotal} />
                    </Typography>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(QuoteTotal);
